import { DevMode } from '@chipinside/devmode'
import { Search } from '@chipinside/frontend'
import { Translate } from '@chipinside/translate'
import { Box, Button, Dialog, DialogContent, InputAdornment, Stack } from '@mui/material'
import { Search as SearchIcon } from 'lucide-react'

import { useAdvancedSearch } from '#/components/AdvancedSearch/Logic/useAdvancedSearch'
import { CategoryFilter } from '#/components/AdvancedSearch/View/CategoryFilter'
import { SearchFooter } from '#/components/AdvancedSearch/View/Footer'
import { NoResultsFound } from '#/components/AdvancedSearch/View/NoResultsFound'
import { ResultGroups } from '#/components/AdvancedSearch/View/ResultGroups'
import GlobalSearchInput, {
  GlobalSearchShortcut,
} from '#/components/AdvancedSearch/View/SearchTrigger'
import { CircularLoader } from '#/components/Loader/Circular'
import { useFarmName } from '#/store/ducks/farm'
import { Sentry, SentryEvent } from '#/store/services/Sentry'

export function SearchBar() {
  const farmName = useFarmName()

  const {
    activeCategory,
    activeIndex,
    categoryOptions,
    groups,
    handleClearRecent,
    handleClearSearch,
    handleClose,
    handleInputChange,
    isFarmContext,
    isValidatingEntities,
    open,
    recentSearchScope,
    searchInput,
    searchResetKey,
    setActiveCategory,
    setActiveIndex,
    setOpen,
    visibleCount,
  } = useAdvancedSearch()

  const hasSearch = Boolean(searchInput)

  function renderResults() {
    if (isValidatingEntities) {
      return <CircularLoader minHeight={200} />
    }
    if (hasSearch && visibleCount === 0) {
      return (
        <NoResultsFound
          title={Translate({ messageKey: 'search_no_recent_title' })}
          subtitle={Translate({
            messageKey: 'term_no_results_found',
            params: { term: searchInput },
          })}
        />
      )
    }
    if (!hasSearch && groups.length === 0) {
      return (
        <NoResultsFound
          title={Translate({ messageKey: 'search_no_recent_title' })}
          subtitle={Translate({ messageKey: 'search_no_recent_example' })}
        />
      )
    }
    return (
      <ResultGroups
        activeIndex={activeIndex}
        groups={groups}
        handleClose={handleClose}
        onClearRecent={handleClearRecent}
        onViewAll={setActiveCategory}
        scope={recentSearchScope}
        searchTerm={searchInput}
        setActiveIndex={setActiveIndex}
      />
    )
  }

  return (
    <>
      <GlobalSearchInput
        onClick={() => {
          Sentry.metrics({ event: SentryEvent.SEARCH_OPEN })
          setOpen(true)
        }}
      />
      <Dialog
        open={open}
        fullWidth
        maxWidth="lg"
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 4,
              margin: { sm: '32px', xs: 0 },
              overflow: 'hidden',
              width: { sm: 'calc(100% - 64px)', xs: 'calc(100% - 32px)' },
            },
          },
        }}
      >
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', overflowY: 'hidden', p: 0 }}>
          <Box sx={{ backgroundColor: 'background.paper', p: 2 }}>
            <Search
              key={searchResetKey}
              autoComplete="off"
              autoFocus
              variant="outlined"
              startAdornment={
                <InputAdornment position="start">
                  <DevMode
                    placement="bottom-start"
                    items={[
                      { title: 'Pesquisável por ID', value: 'Sim' },
                      { title: 'Páginas pesquisáveis', value: 'Todas (System, Farm)' },
                      { title: 'Fazenda', value: farmName ?? 'Total' },
                    ]}
                  >
                    <SearchIcon />
                  </DevMode>
                </InputAdornment>
              }
              endAdornment={
                <Stack direction="row" sx={{ alignItems: 'center' }}>
                  {searchInput && (
                    <Button variant="text" size="small" onClick={handleClearSearch}>
                      {Translate({ messageKey: 'clear' })}
                    </Button>
                  )}
                  <GlobalSearchShortcut text="Esc" onClick={() => setOpen(false)} />
                </Stack>
              }
              value={searchInput}
              cb={({ search }) => handleInputChange(search)}
              delay={500}
              placeholder={Translate({
                messageKey: isFarmContext ? 'global_search' : 'global_search_pages_and_farms',
              })}
              label={Translate({ messageKey: 'what_are_you_looking_for' })}
              slotProps={{
                input: { sx: { borderRadius: 2 } },
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', flex: 1, minHeight: 0, px: 2 }}>
            {hasSearch && !isValidatingEntities && (
              <CategoryFilter
                activeCategory={activeCategory}
                onSelect={setActiveCategory}
                options={categoryOptions}
              />
            )}
            <Box
              sx={{
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                height: '60vh',
                minWidth: 0,
                overflowY: 'auto',
                px: { sm: 2, xs: 0 },
              }}
            >
              {renderResults()}
            </Box>
          </Box>
          <SearchFooter resultCount={visibleCount} hasSearch={hasSearch} />
        </DialogContent>
      </Dialog>
    </>
  )
}
